import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref, watch } from 'vue';

import authorization from '@rygen/rygen-ui/authorization';
import {
  CurrentUser,
  DomainBranding,
  DomainReference,
  DomainTree,
  ErrorResponse,
  Module,
  Optional,
  UUID,
} from '@rygen/rygen-ui/model';
import { resolveDomainContextFromDomainNaturalId } from '@rygen/rygen-ui/util';

import api from '@/api';
import { useDomainStore } from '@/stores/Domain';

export const ANONYMOUS_USERNAME = 'Anonymous Carrier User';

export const DOMAIN_LS_KEY = 'corsair-tms.domain-context';

const getCurrentDomain = (): number | undefined => {
  const domainIdString = localStorage.getItem(DOMAIN_LS_KEY);
  return domainIdString ? Number.parseInt(domainIdString, 10) : undefined;
};

export interface CurrentUserStore {
  accentColor: Ref<string>;
  applyUrlDomainContext: (module: Module) => Promise<boolean>;
  attemptDomainChange: (newTargetDomainId: number) => void;
  availableDomainCacheInvalidated: ComputedRef<boolean>;
  availableDomainMap: ComputedRef<Map<number, DomainTree>>;
  availableDomains: ComputedRef<DomainTree[]>;
  clearCurrentDomain: () => void;
  clearSelectedDomainIdOverride: () => void;
  confirmDomainChange: () => void;
  currentUser: ComputedRef<CurrentUser | undefined>;
  domainContextIsInitialized: ComputedRef<boolean>;
  getAvailableDomains: () => Promise<DomainTree[]>;
  handleRequiredDomainRedirect: (error: unknown) => Promise<boolean>;
  initializeDomainContext: () => Promise<void>;
  is3PLClient: ComputedRef<boolean>;
  is3PLServiceProvider: ComputedRef<boolean>;
  is3PLClientOrServiceProvider: ComputedRef<boolean>;
  isAuthenticatedUser: ComputedRef<boolean>;
  isRygenDirectClient: ComputedRef<boolean>;
  loadDomainForUnauthenticatedUser: (
    domainId: number,
    accessToken: UUID,
  ) => Promise<void>;
  notifyDomainChange: () => void;
  revertDomainChange: () => void;
  selectedDomainBranding: ComputedRef<DomainBranding | undefined>;
  selectedDomain: ComputedRef<DomainReference | undefined>;
  selectedDomainId: ComputedRef<number | undefined>;
  setSelectedDomain: (selectedDomain: DomainReference) => void;
  setSelectedDomainId: (selectedDomainId: number | undefined) => void;
  setSelectedDomainIdOverride: (selectedDomainIdOverride: number) => void;
  targetDomain: ComputedRef<DomainReference | undefined>;
  targetDomainId: ComputedRef<number | undefined>;
  waitForDomainContextInitialization: () => Promise<void>;
}

export const _useCurrentUserStore = defineStore(
  'currentUser',
  (): CurrentUserStore => {
    const { domainBrandings, loadDomainBranding, updateBranding } =
      useDomainStore();

    const _availableDomainCacheInvalidated = ref(true);
    const _availableDomains: Ref<DomainTree[]> = ref([]);
    const _currentUser: Ref<CurrentUser | undefined> = ref();
    const _selectedDomainId = ref(getCurrentDomain());
    const _selectedDomainIdOverride: Ref<Optional<number>> = ref();
    const _targetDomainId: Ref<number | undefined> = ref();

    const accentColor = computed((): string => {
      return (
        selectedDomainBranding?.value?.accentColor ??
        DomainBranding.DEFAULT_ACCENT_COLOR
      );
    });

    const availableDomainCacheInvalidated = computed(() => {
      return _availableDomainCacheInvalidated.value;
    });

    const availableDomainMap = computed(() => {
      return DomainTree.toMap(_availableDomains.value);
    });

    const availableDomains = computed(() => _availableDomains.value);

    const currentUser = computed(() => _currentUser.value);

    const domainContextIsInitialized = computed(() => {
      return _currentUser.value !== undefined;
    });

    const is3PLClient = computed(() => {
      return (
        selectedDomain.value?.domainType === 'Client' &&
        selectedDomain.value?.realm?.realmType ===
          'THIRD_PARTY_LOGISTICS_PROVIDER'
      );
    });

    const is3PLServiceProvider = computed(() => {
      return (
        selectedDomain.value?.domainType === 'TopLevelDomain' &&
        selectedDomain.value?.realm?.realmType ===
          'THIRD_PARTY_LOGISTICS_PROVIDER'
      );
    });

    const is3PLClientOrServiceProvider = computed(() => {
      return is3PLClient.value || is3PLServiceProvider.value;
    });

    const isAuthenticatedUser = computed(() => {
      return (
        !!currentUser.value &&
        currentUser.value.user?.username !== ANONYMOUS_USERNAME
      );
    });

    const isRygenDirectClient = computed(() => {
      return (
        !!currentUser.value &&
        !currentUser.value.selectedDomainThirdPartyLogisticsProvider
      );
    });

    const selectedDomainId = computed(() => {
      return _selectedDomainIdOverride.value ?? _selectedDomainId.value;
    });

    const selectedDomain = computed(() => {
      if (!selectedDomainId.value) {
        return undefined;
      }

      const selectedDomainTree = availableDomainMap.value.get(
        selectedDomainId.value,
      );
      if (!selectedDomainTree) {
        return undefined;
      }

      return selectedDomainTree.asDomainReference();
    });

    const selectedDomainBranding = computed(
      () => domainBrandings.value[selectedDomainId.value ?? 0]?.data,
    );

    const targetDomainId = computed(() => _targetDomainId.value);

    const targetDomain = computed(() => {
      if (!_targetDomainId.value) {
        return undefined;
      }

      const targetDomainTree = availableDomainMap.value.get(
        _targetDomainId.value,
      );
      if (!targetDomainTree) {
        return undefined;
      }

      return targetDomainTree.asDomainReference();
    });

    const setSelectedDomain = (newSelectedDomain: DomainReference) => {
      if (!newSelectedDomain.id) {
        throw new Error('DomainReference must have an id', {
          cause: newSelectedDomain,
        });
      }

      setSelectedDomainId(newSelectedDomain.id);
    };

    const setSelectedDomainId = (selectedDomainId: number | undefined) => {
      if (selectedDomainId) {
        localStorage.setItem(DOMAIN_LS_KEY, selectedDomainId.toString());
      } else {
        localStorage.removeItem(DOMAIN_LS_KEY);
      }

      _selectedDomainId.value = selectedDomainId;

      const url = new URL(window.location.href);
      url.searchParams.delete('domain-context');
      window.history.replaceState({}, '', url.toString());

      initializeDomainContext();
    };

    const applyUrlDomainContext = async (module: Module): Promise<boolean> => {
      const naturalId = new URLSearchParams(window.location.search).get(
        'domain-context',
      );

      const url = new URL(window.location.href);
      url.searchParams.delete('domain-context');
      window.history.replaceState({}, '', url.toString());

      if (!naturalId) {
        return false;
      }

      const allDomains = await api.domain.getAllDomains();

      const resolvedDomainId = resolveDomainContextFromDomainNaturalId(
        module,
        naturalId,
        allDomains,
      );
      if (!resolvedDomainId) {
        return false;
      }

      if (availableDomainMap.value.has(resolvedDomainId)) {
        setSelectedDomainId(resolvedDomainId);
      } else {
        setSelectedDomainId(undefined);
      }

      await initializeDomainContext();
      return true;
    };

    const loadDomainForUnauthenticatedUser = async (
      domainId: number,
      accessToken: UUID,
    ) => {
      const isDomainLoaded = availableDomainMap.value.has(domainId);
      if (isDomainLoaded) {
        console.info(`Domain ${domainId} is already loaded`);
        return;
      }

      try {
        _availableDomains.value = await api.carrierPortal.getDomainTree(
          domainId,
          accessToken,
        );
        _availableDomainCacheInvalidated.value = false;

        setSelectedDomainIdOverride(domainId);
        loadDomainBrandingForUnauthenticatedUser(domainId, accessToken);
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        console.error(`Error loading domain ${domainId}`, e);
      }
    };

    const loadDomainBrandingForUnauthenticatedUser = async (
      domainId: number,
      accessToken: UUID,
    ) => {
      const existingDomainBranding = domainBrandings.value[domainId];
      if (existingDomainBranding) {
        return;
      }

      try {
        const domainBranding = await api.carrierPortal.getDomainBranding(
          domainId,
          accessToken,
        );
        updateBranding(domainId, domainBranding);
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        if (axios.isAxiosError(e) && e.response?.status === 404) {
          // No domain branding is fine
          return;
        }
        console.error(
          `Error loading domain branding for domain ${domainId}`,
          e,
        );
      }
    };

    const getAvailableDomains = async () => {
      if (!_availableDomainCacheInvalidated.value) {
        return availableDomains.value;
      }

      const domains = await api.domain.getTree();
      _availableDomains.value = domains;
      _availableDomainCacheInvalidated.value = false;

      availableDomainMap.value.forEach((tree, id) => {
        loadDomainBranding(id).then((loadable) => {
          if (loadable.data) {
            tree.branding = loadable.data;
          }
        });
      });

      return domains;
    };

    const initializeDomainContext = async () => {
      if (_selectedDomainId.value === undefined) {
        return;
      }

      try {
        const newCurrentUser = await api.user.getCurrentUser();
        authorization.setCurrentUser(newCurrentUser);
        _currentUser.value = newCurrentUser;

        void loadDomainBranding(_selectedDomainId.value);
      } catch (e) {
        console.warn(
          'Could not initialize user domain context with Domain Id',
          _selectedDomainId.value,
          e,
        );
        _selectedDomainId.value = undefined;
      }
    };

    const notifyDomainChange = () => {
      _availableDomainCacheInvalidated.value = true;
    };

    const attemptDomainChange = (targetDomainId: number) => {
      if (targetDomainId !== _selectedDomainId.value) {
        _targetDomainId.value = targetDomainId;
      }
    };

    const confirmDomainChange = () => {
      if (_targetDomainId.value !== undefined) {
        setSelectedDomainId(_targetDomainId.value);
        _targetDomainId.value = undefined;
      }
    };

    const revertDomainChange = () => {
      _targetDomainId.value = undefined;
    };

    const setSelectedDomainIdOverride = (selectedDomainIdOverride: number) => {
      _selectedDomainIdOverride.value = selectedDomainIdOverride;
    };

    const clearSelectedDomainIdOverride = () => {
      _selectedDomainIdOverride.value = undefined;
    };

    const clearCurrentDomain = () => {
      _selectedDomainId.value = undefined;
    };

    /**
     * Handle a required domain redirect, if required
     *
     * @returns true if a required domain redirect occured, false otherwise.
     */
    const handleRequiredDomainRedirect = async (
      error: unknown,
    ): Promise<boolean> => {
      if (!axios.isAxiosError(error)) {
        return false;
      }

      const responseData = error.response?.data as ErrorResponse;
      const targetDomainIdStr = responseData?.errorDetails?.targetDomainId as
        string | undefined;
      if (error.response?.status === 401 && targetDomainIdStr) {
        const targetDomainId = Number.parseInt(targetDomainIdStr);
        await attemptDomainChange(targetDomainId);
        return true;
      }

      return false;
    };

    const waitForDomainContextInitialization = (): Promise<void> => {
      return new Promise((resolve) => {
        console.info('Waiting for domain context initalization...');

        const unwatch = watch(
          domainContextIsInitialized,
          (isInitialized: boolean) => {
            if (isInitialized) {
              console.info(
                'Domain context initialized, proceeding to route...',
              );
              unwatch();
              resolve();
            }
          },
        );
      });
    };

    return {
      accentColor,
      applyUrlDomainContext,
      attemptDomainChange,
      availableDomainCacheInvalidated,
      availableDomainMap,
      availableDomains,
      clearCurrentDomain,
      clearSelectedDomainIdOverride,
      confirmDomainChange,
      currentUser,
      domainContextIsInitialized,
      getAvailableDomains,
      handleRequiredDomainRedirect,
      initializeDomainContext,
      is3PLClient,
      is3PLServiceProvider,
      is3PLClientOrServiceProvider,
      isAuthenticatedUser,
      isRygenDirectClient,
      loadDomainForUnauthenticatedUser,
      notifyDomainChange,
      revertDomainChange,
      selectedDomain,
      selectedDomainBranding,
      selectedDomainId,
      setSelectedDomain,
      setSelectedDomainId,
      setSelectedDomainIdOverride,
      targetDomain,
      targetDomainId,
      waitForDomainContextInitialization,
    };
  },
);

export const useCurrentUserStore = (): CurrentUserStore => {
  const store = _useCurrentUserStore();
  const {
    accentColor,
    availableDomainCacheInvalidated,
    availableDomainMap,
    availableDomains,
    currentUser,
    domainContextIsInitialized,
    is3PLClient,
    is3PLServiceProvider,
    is3PLClientOrServiceProvider,
    isAuthenticatedUser,
    isRygenDirectClient,
    selectedDomain,
    selectedDomainBranding,
    selectedDomainId,
    targetDomain,
    targetDomainId,
  } = storeToRefs(store);
  const {
    applyUrlDomainContext,
    attemptDomainChange,
    clearCurrentDomain,
    clearSelectedDomainIdOverride,
    confirmDomainChange,
    getAvailableDomains,
    handleRequiredDomainRedirect,
    initializeDomainContext,
    loadDomainForUnauthenticatedUser,
    notifyDomainChange,
    revertDomainChange,
    setSelectedDomain,
    setSelectedDomainId,
    setSelectedDomainIdOverride,
    waitForDomainContextInitialization,
  } = store;

  return {
    accentColor,
    applyUrlDomainContext,
    attemptDomainChange,
    availableDomainCacheInvalidated,
    availableDomainMap,
    availableDomains,
    clearCurrentDomain,
    clearSelectedDomainIdOverride,
    confirmDomainChange,
    currentUser,
    domainContextIsInitialized,
    getAvailableDomains,
    handleRequiredDomainRedirect,
    initializeDomainContext,
    is3PLClient,
    is3PLServiceProvider,
    is3PLClientOrServiceProvider,
    isAuthenticatedUser,
    isRygenDirectClient,
    loadDomainForUnauthenticatedUser,
    notifyDomainChange,
    revertDomainChange,
    selectedDomain,
    selectedDomainBranding,
    selectedDomainId,
    setSelectedDomain,
    setSelectedDomainId,
    setSelectedDomainIdOverride,
    targetDomain,
    targetDomainId,
    waitForDomainContextInitialization,
  };
};
